/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useComponentProps } from '@sitecore-content-sdk/nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { ScrollButton } from '../lists/Tab';
import { useScrollableTabs } from 'lib/hooks/useScrollableTabs';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const ArticleCategoryTabs = (props)=>{
    const isEditing = useIsEditing();
    const ctxProps = useComponentProps(props?.rendering?.uid);
    const staticProps = props?.staticProps ?? ctxProps?.staticProps;
    const current = staticProps?.currentPage;
    const isLanding = current?.template?.name === 'Article Landing Page';
    const landingNode = isLanding ? current : current.parent;
    const tabItems = landingNode?.children?.results || [];
    const { RenderingIdentifier } = props?.params || {};
    const { containerRef, isScrollable, isLeftArrowVisible, isRightArrowVisible, handleScroll } = useScrollableTabs();
    const currentPath = current?.url?.path;
    const isLandingActive = currentPath === landingNode?.url?.path;
    const { base, tabsContainerWrapper, tabsWrapper, tabLinkWrapper, tabLink } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("section", {
        className: base(),
        "data-component": "authorable/shared/page-specific/articlecategorytabs",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-article-category-tabs-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: tabsContainerWrapper(),
            children: [
                isScrollable && isLeftArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                    direction: "left",
                    onClick: ()=>handleScroll('left'),
                    tabListId: "articletab-list"
                }),
                /*#__PURE__*/ _jsxs("ul", {
                    className: tabsWrapper(),
                    ref: containerRef,
                    role: "tablist",
                    id: "articletab-list",
                    "aria-label": "Article Tab navigation",
                    children: [
                        /*#__PURE__*/ _jsx("li", {
                            className: tabLinkWrapper({
                                isActive: isLandingActive
                            }),
                            role: "presentation",
                            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "default",
                                className: tabLink({
                                    isActive: isLandingActive
                                }),
                                field: {
                                    href: landingNode?.url?.path
                                },
                                id: `articletab-${landingNode?.id}`,
                                role: "tab",
                                "aria-controls": `articletab-content-${landingNode?.id}`,
                                "aria-selected": isLandingActive,
                                ...getTestProps(`category-link-0`),
                                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    field: {
                                        value: landingNode?.categoryName?.value
                                    },
                                    ...getTestProps(`category-name-0`)
                                })
                            })
                        }),
                        tabItems.map((item, index)=>{
                            const isActive = currentPath === item.url?.path;
                            return /*#__PURE__*/ _jsx("li", {
                                className: tabLinkWrapper({
                                    isActive
                                }),
                                role: "tab",
                                "aria-controls": `articletab-content-${item.id}`,
                                "aria-selected": isActive,
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "default",
                                    className: tabLink({
                                        isActive
                                    }),
                                    ctaVariant: "custom",
                                    field: {
                                        href: item.url?.path
                                    },
                                    id: `articletab-${item.id}`,
                                    tabIndex: -1,
                                    ...getTestProps(`category-link-${index + 1}`),
                                    children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: {
                                            value: item.categoryName.value
                                        },
                                        ...getTestProps(`category-name-${index + 1}`)
                                    })
                                })
                            }, item.id);
                        })
                    ]
                }),
                isScrollable && isRightArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                    direction: "right",
                    onClick: ()=>handleScroll('right'),
                    tabListId: "articletab-list",
                    ...getTestProps(`scroll-button`)
                })
            ]
        })
    });
};
export default ArticleCategoryTabs;
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [],
        tabsContainerWrapper: [
            'container',
            'flex',
            'items-center',
            'gap-space-between-micro',
            'relative',
            'pt-margin-less',
            'pb-0'
        ],
        tabArrows: [
            'absolute',
            'py-4',
            'px-0',
            'w-[44px]',
            'h-[68px]'
        ],
        tabsWrapper: [
            'container',
            'overflow-x-auto',
            'bg-component-tab-color-bg',
            'rounded-component-tab-border-radius',
            'flex',
            'px-component-tab-layout-container-padding-x',
            'py-component-tab-layout-container-padding-y'
        ],
        tabLinkWrapper: [
            'gap-spacing-spacing-8',
            'border-l-component-tab-border-width-left',
            'border-r-component-tab-border-width-right',
            'border-t-component-tab-border-width-top',
            'border-b-component-tab-border-width-bottom',
            'border-component-tab-color-border',
            'hover:border-component-tab-color-border-hover',
            'rounded-component-tab-border-radius',
            'focus:ring-color-border-border-focus',
            'focus:border-component-tab-color-border-focused'
        ],
        tabLink: [
            'flex',
            'items-center',
            'justify-center',
            'px-spacing-spacing-24',
            'py-spacing-spacing-16',
            'min-w-component-tab-layout-min-width',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'leading-5',
            'font-semibold',
            'text-component-tab-color-text',
            'bg-component-tab-color-bg',
            'hover:text-component-tab-color-text-hover',
            'hover:bg-component-tab-color-bg-hover',
            'focus:bg-component-tab-color-bg-focused',
            'focus:text-component-tab-color-text-focused',
            'whitespace-nowrap'
        ]
    },
    variants: {
        isActive: {
            true: {
                tabLinkWrapper: [
                    'border-component-tab-color-border-active'
                ],
                tabLink: [
                    'text-component-tab-color-text-active',
                    'bg-component-tab-color-bg-active'
                ]
            }
        },
        disabled: {
            true: {
                tabArrows: [
                    'opacity-50',
                    'cursor-not-allowed',
                    'hover:bg-transparent'
                ]
            }
        },
        right: {
            true: {
                tabArrows: [
                    'right-[-1px]',
                    'bg-gradient-to-l',
                    'from-color-bg-bg to-color-white-alpha-0 from-[9.61%] to-100%'
                ]
            },
            false: {
                tabArrows: [
                    'left-[-1px]',
                    'bg-gradient-to-r',
                    'from-color-bg-bg to-color-white-alpha-0 from-[9.61%] to-100%'
                ]
            }
        }
    }
});
